// @ts-nocheck
import PropTypes from "prop-types";
import {useLayoutEffect, useRef, useState} from "react";
import * as styles from "./_rouletteBoardCls";
import NumberGrid from "./NumberGrid";

/**
 * RouletteBoardThirtyEight - Reusable betting board component for 38-animal lottery games
 *
 * This component renders a roulette-style betting board with 38 animals (00-36 + special animals)
 * and various betting options (colors, even/odd, dozens, columns, ranges).
 *
 * Supports multiple games through the gameIdString prop:
 * - ruleta-activa
 * - pal-caserito
 * - Any future games configured with boardType: 'thirty-eight' in lottery.util.js
 *
 * @param {string} gameIdString - The game identifier (e.g., 'ruleta-activa', 'pal-caserito')
 * @param {function} onBetClick - Callback when a bet is placed
 * @param {array} placedBets - Array of currently placed bets
 * @param {number} maxBetPerOption - Maximum bet amount per option
 * @param {function} onMaxBetReached - Callback when max bet is reached
 * @param {boolean} isDeletionMode - Whether the board is in deletion mode
 */
const RouletteBoardThirtyEight = ({
  gameIdString,
  onBetClick,
  placedBets,
  maxBetPerOption = 1000,
  onMaxBetReached,
  isDeletionMode = false,
  figures = [], // New prop for API figures
}) => {
  const handleBetClick = (betType, value, odds) => {
    // If in deletion mode, skip limit check and let parent handle it
    if (isDeletionMode) {
      if (onBetClick) {
        onBetClick({betType, value, odds});
      }
      return;
    }

    // Only check max bet limit when NOT in deletion mode
    const currentTotal = getTotalBetForOption(value);
    if (currentTotal >= maxBetPerOption) {
      // Notify parent component about max bet reached
      if (onMaxBetReached) {
        onMaxBetReached(value, maxBetPerOption);
      }
      return; // Don't proceed with bet
    }

    if (onBetClick) {
      onBetClick({betType, value, odds});
    }
  };

  // Helper function to get fichas for a specific bet value
  const getFichasForBet = betValue => {
    const bet = placedBets?.find(bet => bet.animalId === betValue);
    return bet ? bet.fichas : [];
  };

  // Helper function to calculate total bet for a specific option
  const getTotalBetForOption = betValue => {
    const fichas = getFichasForBet(betValue);
    return fichas.reduce((sum, ficha) => sum + ficha.value, 0);
  };

  // Helper function to render ficha chip with sum in the middle
  const renderBetChip = betValue => {
    const fichas = getFichasForBet(betValue);
    if (fichas.length === 0) return null;

    const totalBet = getTotalBetForOption(betValue);

    return (
      <div
        className={`${styles.betChipCls} ${isDeletionMode ? styles.betChipDeletionModeCls : ""}`}
        title={isDeletionMode ? `Click para eliminar ($${totalBet})` : `Total apostado: ${totalBet}`}
      >
        <div className={styles.betChipAmountCls}>{totalBet}</div>
      </div>
    );
  };

  const scaledRef = useRef(null);
  const containerRef = useRef(null);
  const [dims, setDims] = useState({height: undefined, width: undefined});

  // Dynamically set the container height to the scaled content height so that
  // the outer container collapses exactly to what is visually rendered.
  useLayoutEffect(() => {
    function measure() {
      if (!scaledRef.current) return;
      const rect = scaledRef.current.getBoundingClientRect();
      setDims({height: rect.height, width: rect.width});
    }
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [placedBets?.length]);

  return (
    <div
      className={styles.boardContainerCls}
      ref={containerRef}
      style={{height: dims.height, width: dims.width}}
    >
      <div
        className={styles.scaledContentCls}
        ref={scaledRef}
      >
        {/* Main Row: Left Section and Right Column */}
        <div className={styles.mainRowCls}>
          {/* Left Section */}
          <div className={styles.leftSectionCls}>
            {/* Left side of left section - 6 betting options */}
            <div className={styles.leftSideOptionsCls}>
              <button
                type="button"
                className={`${styles.betOptionCls} ${styles.option1Cls}`}
                onClick={() => handleBetClick("range", "1-18", "2:1")}
              >
                <span className={styles.verticalTextCls}>1-18</span>
                {renderBetChip("1-18")}
              </button>
              <button
                type="button"
                className={`${styles.betOptionCls} ${styles.option2Cls}`}
                onClick={() => handleBetClick("evenOdd", "PAR", "2:1")}
              >
                <span className={styles.verticalTextCls}>PAR</span>
                {renderBetChip("PAR")}
              </button>
              <button
                type="button"
                className={`${styles.betOptionCls} ${styles.option3Cls}`}
                onClick={() => handleBetClick("color", "ROJO", "2:1")}
              >
                <span className={styles.verticalTextCls}>ROJO</span>
                {renderBetChip("ROJO")}
              </button>
              <button
                type="button"
                className={`${styles.betOptionCls} ${styles.option4Cls}`}
                onClick={() => handleBetClick("color", "NEGRO", "2:1")}
              >
                <span className={styles.verticalTextCls}>NEGRO</span>
                {renderBetChip("NEGRO")}
              </button>
              <button
                type="button"
                className={`${styles.betOptionCls} ${styles.option5Cls}`}
                onClick={() => handleBetClick("evenOdd", "IMPAR", "2:1")}
              >
                <span className={styles.verticalTextCls}>IMPAR</span>
                {renderBetChip("IMPAR")}
              </button>
              <button
                type="button"
                className={`${styles.betOptionCls} ${styles.option6Cls}`}
                onClick={() => handleBetClick("range", "19-36", "2:1")}
              >
                <span className={styles.verticalTextCls}>19-36</span>
                {renderBetChip("19-36")}
              </button>
            </div>

            {/* Right side of left section - 3 dozen options */}
            <div className={styles.rightSideOptionsCls}>
              <button
                type="button"
                className={`${styles.betOptionCls} ${styles.dozen1Cls}`}
                onClick={() => handleBetClick("dozen", "1-12", "3:1")}
              >
                <span className={styles.verticalTextCls}>1ª 12</span>
                {renderBetChip("1-12")}
              </button>
              <button
                type="button"
                className={`${styles.betOptionCls} ${styles.dozen2Cls}`}
                onClick={() => handleBetClick("dozen", "13-24", "3:1")}
              >
                <span className={styles.verticalTextCls}>2ª 12</span>
                {renderBetChip("13-24")}
              </button>
              <button
                type="button"
                className={`${styles.betOptionCls} ${styles.dozen3Cls}`}
                onClick={() => handleBetClick("dozen", "25-36", "3:1")}
              >
                <span className={styles.verticalTextCls}>3ª 12</span>
                {renderBetChip("25-36")}
              </button>
            </div>
          </div>

          {/* Right Column: Contains Number Grid and Bottom Section */}
          <div className={styles.rightColumnCls}>
            {/* Right Section - Number Grid */}
            <div className={styles.rightSectionCls}>
              <NumberGrid
                gameIdString={gameIdString}
                onNumberClick={onBetClick}
                placedBets={placedBets}
                maxBetPerOption={maxBetPerOption}
                onMaxBetReached={onMaxBetReached}
                isDeletionMode={isDeletionMode}
                figures={figures}
              />
            </div>

            {/* Bottom Section - Column Bets */}
            <div className={styles.bottomSectionCls}>
              <button
                type="button"
                className={`${styles.columnBetCls} ${styles.column1Cls}`}
                onClick={() => handleBetClick("column", "2-1-col1", "3:1")}
              >
                2-1
                {renderBetChip("2-1-col1")}
              </button>
              <button
                type="button"
                className={`${styles.columnBetCls} ${styles.column2Cls}`}
                onClick={() => handleBetClick("column", "2-1-col2", "3:1")}
              >
                2-1
                {renderBetChip("2-1-col2")}
              </button>
              <button
                type="button"
                className={`${styles.columnBetCls} ${styles.column3Cls}`}
                onClick={() => handleBetClick("column", "2-1-col3", "3:1")}
              >
                2-1
                {renderBetChip("2-1-col3")}
              </button>
            </div>
          </div>
        </div>
        {/* end mainRow */}
      </div>
      {/* end scaledContent */}
    </div>
  );
};

RouletteBoardThirtyEight.propTypes = {
  gameIdString: PropTypes.string.isRequired,
  onBetClick: PropTypes.func,
  placedBets: PropTypes.arrayOf(
    PropTypes.shape({
      animalId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      animalName: PropTypes.string,
      fichas: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          value: PropTypes.number,
          name: PropTypes.string,
        })
      ),
    })
  ),
  onRemoveFicha: PropTypes.func,
  maxBetPerOption: PropTypes.number,
  onMaxBetReached: PropTypes.func,
  isDeletionMode: PropTypes.bool,
  figures: PropTypes.arrayOf(PropTypes.object),
};

export default RouletteBoardThirtyEight;
